import React, { useState, useEffect, useRef } from 'react';
import Navbar from '../../components/navbar/navbar';
import Sidebar from '../../components/sidebar/sidebar';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { Card } from 'primereact/card';
import { Chart } from 'primereact/chart';
import { Badge } from 'primereact/badge';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
    API_URLS,
    obtenerProductos,
    obtenerUsuarios
} from '../../Services/servicioURL';
import { useNavigate } from 'react-router-dom';


interface InventarioItem {
    cve_producto: string;
    descripcion: string;
    modelo: string;
}

interface Usuario {
    id: number;
    nombre: string;
}

interface Sucursal {
    id: string;
    nombre: string;
}

interface Departamento {
    cve: string;
    nombre: string;
}

interface ProductoSalida {
    cve_producto: string;
    descripcion: string;
    total_salidas: number;
}

interface ProductoEntrada {
    cve_producto: string;
    descripcion: string;
    total_entradas: number;
}

interface PeticionType {
    folio: number;
    cveemp: string;
    fecha_hora: string;
    titulo: string;
    justificacion: string;
    sucursal: string;
    departamento: string;
    empleado?: {
        cveemp: string;
        nomemp: string;
    };
}

interface ActividadReciente {
    id: string;
    tipo: 'entrada' | 'salida' | 'mantenimiento' | 'peticion' | 'usuario' | 'producto' | 'sucursal' | 'departamento' | 'proveedor' | 'marca' | 'linea' | 'sublinea' | 'unidad';
    accion: 'crear' | 'actualizar' | 'eliminar' | 'registrar';
    descripcion: string;
    fecha: string;
    usuario?: string;
    detalle?: string;
}

interface SaldoProducto {
    cve_producto: string;
    entradas: number;
    salidas: number;
    saldo: number;
}

const Inicio: React.FC = () => {
    const [productosCatalogo, setProductosCatalogo] = useState<InventarioItem[]>([]);
    const [usuariosCatalogo, setUsuariosCatalogo] = useState<Usuario[]>([]);
    const [sucursalesCatalogo, setSucursalesCatalogo] = useState<Sucursal[]>([]);
    const navigate = useNavigate();
    const [departamentosCatalogo, setDepartamentosCatalogo] = useState<Departamento[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [topProductosSalidas, setTopProductosSalidas] = useState<ProductoSalida[]>([]);
    const [topProductosEntradas, setTopProductosEntradas] = useState<ProductoEntrada[]>([]);
    const [totalProductos, setTotalProductos] = useState<number>(0);
    const [productosConExistencia, setProductosConExistencia] = useState<number>(0);
    const [productosSinExistencia, setProductosSinExistencia] = useState<number>(0);
    const [productosConEntradas, setProductosConEntradas] = useState<number>(0);
    const [productosSinEntradas, setProductosSinEntradas] = useState<number>(0);
    const [actividadReciente, setActividadReciente] = useState<ActividadReciente[]>([]);
    const [chartDataSalidas, setChartDataSalidas] = useState<any>({});
    const [chartDataEntradas, setChartDataEntradas] = useState<any>({});
    const [chartDataLineal, setChartDataLineal] = useState<any>({});
    const [tipoGrafica, setTipoGrafica] = useState<'entradas' | 'salidas'>('entradas');
    
    const [chartOptions] = useState({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom' as const,
            }
        }
    });

     
    const [lineOptions] = useState({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top' as const,
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    stepSize: 1
                }
            }
        }
    });

    // Estados para el modal
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const [selectedMovimiento, setSelectedMovimiento] = useState<any>(null);
    const [movimientoType, setMovimientoType] = useState<'entrada' | 'salida' | 'mantenimiento' | 'peticion'>('entrada');
    const [entradasFull, setEntradasFull] = useState<any[]>([]);
    const [salidasFull, setSalidasFull] = useState<any[]>([]);
    const [mantenimientosFull, setMantenimientosFull] = useState<any[]>([]);
    const [peticionesFull, setPeticionesFull] = useState<PeticionType[]>([]);

    const toast = useRef<Toast>(null);

    useEffect(() => {
        const fetchAllData = async () => {
            try {
                setLoading(true);
                
                // Cargar catálogos
                const [dataProductos, dataUsuarios, dataSucursales, dataDepartamentos] = await Promise.all([
                    obtenerProductos(API_URLS.productos.obtener),
                    obtenerUsuarios(API_URLS.usuarios.obtener),
                    fetch(API_URLS.sucursales.obtener).then(res => res.json()),
                    fetch(API_URLS.departamentos.obtener).then(res => res.json())
                ]);
                
                const inventarioData = dataProductos.map((producto: any) => ({
                    cve_producto: producto.cve,
                    descripcion: producto.descripcion,
                    modelo: producto.modelo
                }));
                setProductosCatalogo(inventarioData);
                
                const usuariosData = dataUsuarios.map((usuario: any) => ({
                    id: usuario.id,
                    nombre: usuario.nombre
                }));

                setUsuariosCatalogo(usuariosData);
                
                setSucursalesCatalogo(dataSucursales.map((s: any) => ({
                    id: String(s.id),
                    nombre: s.nombre
                })));

                setDepartamentosCatalogo(dataDepartamentos.map((d: any) => ({
                    cve: d.cve,
                    nombre: d.nombre
                })));

                // Cargar movimientos y peticiones
                const [dataEntradas, dataSalidas, dataMantenimientos, dataPeticiones] = await Promise.all([
                    fetch(API_URLS.entradas.obtener).then(res => res.json()),
                    fetch(API_URLS.salidas.obtener).then(res => res.json()),
                    fetch(API_URLS.mantenimientos.obtener).then(res => res.json()),
                    fetch(API_URLS.peticiones.obtener).then(res => res.json()).catch(() => [])
                ]);

                // Guardar datos completos para el modal
                setEntradasFull(dataEntradas);
                setSalidasFull(dataSalidas);
                setMantenimientosFull(dataMantenimientos);
                setPeticionesFull(dataPeticiones);

                // Procesar datos para gráficas y métricas
                await procesarDatos(dataEntradas, dataSalidas, dataMantenimientos, dataPeticiones, inventarioData);

                // Procesar actividad reciente
                await procesarActividadReciente(dataEntradas, dataSalidas, dataMantenimientos, dataPeticiones, inventarioData, usuariosData, dataSucursales, dataDepartamentos);
                
            } catch (error) {
                console.error('Error al cargar datos:', error);
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'No se pudieron cargar los datos del dashboard',
                    life: 3000
                });
            } finally {
                setLoading(false);
            }
        };

        fetchAllData();
    }, []);

   const procesarActividadReciente = async (
    entradas: any[], 
    salidas: any[], 
    mantenimientos: any[], 
    peticiones: PeticionType[],
    productos: any[], 
    usuarios: any[], 
    sucursales: any[], 
    departamentos: any[]
) => {
        const actividades: ActividadReciente[] = [];

        // Procesar entradas
        entradas.forEach((entrada: any) => {
            actividades.push({
                id: `entrada-${entrada.folio}`,
                tipo: 'entrada',
                accion: 'registrar',
                descripcion: `Entrada registrada - Folio: ${entrada.folio}`,
                fecha: entrada.fecha,
                detalle: entrada.observaciones || `${(entrada.entradaDetalle || []).length} productos ingresados`
            });
        });

        // Procesar salidas
        salidas.forEach((salida: any) => {
            actividades.push({
                id: `salida-${salida.folio}`,
                tipo: 'salida',
                accion: 'registrar',
                descripcion: `Salida registrada - Folio: ${salida.folio}`,
                fecha: salida.fecha,
                detalle: salida.observaciones || `${(salida.salidaDetalle || []).length} productos despachados`
            });
        });

        // Procesar mantenimientos
        mantenimientos.forEach((mantenimiento: any) => {
            const tecnico = usuarios.find(u => u.id === mantenimiento.id_tecnico);
            actividades.push({
                id: `mantenimiento-${mantenimiento.folio}`,
                tipo: 'mantenimiento',
                accion: 'registrar',
                descripcion: `Mantenimiento realizado - Folio: ${mantenimiento.folio}`,
                fecha: mantenimiento.fecha,
                usuario: tecnico?.nombre,
                detalle: mantenimiento.observaciones || `${(mantenimiento.mantenimientoDetalle || []).length} productos utilizados`
            });
        });
        
        peticiones.forEach((peticion: PeticionType) => {
            actividades.push({
                id: `peticion-${peticion.folio}`,
                tipo: 'peticion',
                accion: 'registrar',
                descripcion: `Petición registrada - Folio: ${peticion.folio}`,
                fecha: peticion.fecha_hora,
                usuario: peticion.empleado?.nomemp,
                detalle: peticion.titulo
            });
        });

        // Simular actividades de catálogos basándose en datos existentes
        // (En un sistema real, estas vendrían de un log de actividades)
        
        // Actividades de productos (simuladas basándose en fechas recientes)
        productos.slice(0, 5).forEach((producto: any, index: number) => {
            const fechaSimulada = new Date();
            fechaSimulada.setDate(fechaSimulada.getDate() - index - 1);
            actividades.push({
                id: `producto-${producto.cve}`,
                tipo: 'producto',
                accion: 'crear',
                descripcion: `Producto creado: ${producto.descripcion}`,
                fecha: fechaSimulada.toISOString(),
                detalle: `Clave: ${producto.cve}`
            });
        });

        // Actividades de usuarios (simuladas)
        usuarios.slice(0, 3).forEach((usuario: any, index: number) => {
            const fechaSimulada = new Date();
            fechaSimulada.setDate(fechaSimulada.getDate() - index - 2);
            actividades.push({
                id: `usuario-${usuario.id}`,
                tipo: 'usuario',
                accion: 'crear',
                descripcion: `Usuario registrado: ${usuario.nombre}`,
                fecha: fechaSimulada.toISOString(),
                detalle: `ID: ${usuario.id}`
            });
        });

        // Actividades de sucursales (simuladas)
        sucursales.slice(0, 2).forEach((sucursal: any, index: number) => {
            const fechaSimulada = new Date();
            fechaSimulada.setDate(fechaSimulada.getDate() - index - 3);
            actividades.push({
                id: `sucursal-${sucursal.id}`,
                tipo: 'sucursal',
                accion: 'crear',
                descripcion: `Sucursal registrada: ${sucursal.nombre}`,
                fecha: fechaSimulada.toISOString(),
                detalle: `ID: ${sucursal.id}`
            });
        });

        // Actividades de departamentos (simuladas)
        departamentos.slice(0, 2).forEach((departamento: any, index: number) => {
            const fechaSimulada = new Date();
            fechaSimulada.setDate(fechaSimulada.getDate() - index - 4);
            actividades.push({
                id: `departamento-${departamento.cve}`,
                tipo: 'departamento',
                accion: 'crear',
                descripcion: `Departamento registrado: ${departamento.nombre}`,
                fecha: fechaSimulada.toISOString(),
                detalle: `Clave: ${departamento.cve}`
            });
        });

        // Ordenar por fecha (más reciente primero) y tomar las últimas 15
        const actividadesOrdenadas = actividades
            .sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime())
            .slice(0, 15);

        setActividadReciente(actividadesOrdenadas);
    };

    const calcularSaldosProductos = (entradas: any[], salidas: any[], productos: InventarioItem[]): SaldoProducto[] => {
        const saldosMap: { [key: string]: SaldoProducto } = {};

        // Inicializar todos los productos con saldo 0
        productos.forEach(producto => {
            saldosMap[producto.cve_producto] = {
                cve_producto: producto.cve_producto,
                entradas: 0,
                salidas: 0,
                saldo: 0
            };
        });

        // Procesar entradas
        entradas.forEach((entrada: any) => {
            if (Array.isArray(entrada.entradaDetalle)) {
                entrada.entradaDetalle.forEach((detalle: any) => {
                    const cve = detalle.cve_producto;
                    if (saldosMap[cve]) {
                        saldosMap[cve].entradas += detalle.cantidad;
                    }
                });
            }
        });

        // Procesar salidas
        salidas.forEach((salida: any) => {
            if (Array.isArray(salida.salidaDetalle)) {
                salida.salidaDetalle.forEach((detalle: any) => {
                    const cve = detalle.cve_producto;
                    if (saldosMap[cve]) {
                        saldosMap[cve].salidas += detalle.cantidad;
                    }
                });
            }
        });

        // Calcular saldos finales
        Object.values(saldosMap).forEach(saldo => {
            saldo.saldo = saldo.entradas - saldo.salidas;
        });

        return Object.values(saldosMap);
    };

    const procesarDatos = async (entradas: any[], salidas: any[], mantenimientos: any[], peticiones: PeticionType[], productos: InventarioItem[]) => {
        // Calcular saldos de todos los productos
        const saldosProductos = calcularSaldosProductos(entradas, salidas, productos);

        // Procesar entradas por producto
        const entradasPorProducto: { [key: string]: number } = {};
        
        entradas.forEach((entrada: any) => {
            if (Array.isArray(entrada.entradaDetalle)) {
                entrada.entradaDetalle.forEach((detalle: any) => {
                    const cve = detalle.cve_producto;
                    entradasPorProducto[cve] = (entradasPorProducto[cve] || 0) + detalle.cantidad;
                });
            }
        });

        // Procesar top 5 productos con más salidas
        const salidasPorProducto: { [key: string]: number } = {};
        
        salidas.forEach((salida: any) => {
            if (Array.isArray(salida.salidaDetalle)) {
                salida.salidaDetalle.forEach((detalle: any) => {
                    const cve = detalle.cve_producto;
                    salidasPorProducto[cve] = (salidasPorProducto[cve] || 0) + detalle.cantidad;
                });
            }
        });

        const topSalidas = Object.entries(salidasPorProducto)
            .map(([cve, total]) => {
                const producto = productos.find(p => p.cve_producto === cve);
                return {
                    cve_producto: cve,
                    descripcion: producto?.descripcion || 'Producto no encontrado',
                    total_salidas: total
                };
            })
            .sort((a, b) => b.total_salidas - a.total_salidas)
            .slice(0, 5);

        setTopProductosSalidas(topSalidas);

        // Crear gráfica de barras para top salidas
        setChartDataSalidas({
            labels: topSalidas.map(p => p.descripcion.length > 20 ? p.descripcion.substring(0, 20) + '...' : p.descripcion),
            datasets: [
                {
                    label: 'Total Salidas',
                    data: topSalidas.map(p => p.total_salidas),
                    backgroundColor: [
                        '#FF6384',
                        '#36A2EB',
                        '#FFCE56',
                        '#4BC0C0',
                        '#9966FF'
                    ],
                    borderColor: [
                        '#FF6384',
                        '#36A2EB',
                        '#FFCE56',
                        '#4BC0C0',
                        '#9966FF'
                    ],
                    borderWidth: 1
                }
            ]
        });

        // Procesar top 5 productos con más entradas
        const topEntradas = Object.entries(entradasPorProducto)
            .map(([cve, total]) => {
                const producto = productos.find(p => p.cve_producto === cve);
                return {
                    cve_producto: cve,
                    descripcion: producto?.descripcion || 'Producto no encontrado',
                    total_entradas: total
                };
            })
            .sort((a, b) => b.total_entradas - a.total_entradas)
            .slice(0, 5);

        setTopProductosEntradas(topEntradas);

        // Crear gráfica de barras para top entradas (cambiado de pie a bar)
        setChartDataEntradas({
            labels: topEntradas.map(p => p.descripcion.length > 20 ? p.descripcion.substring(0, 20) + '...' : p.descripcion),
            datasets: [
                {
                    label: 'Total Entradas',
                    data: topEntradas.map(p => p.total_entradas),
                    backgroundColor: [
                        '#36A2EB',
                        '#4BC0C0',
                        '#FFCE56',
                        '#FF6384',
                        '#9966FF'
                    ],
                    borderColor: [
                        '#36A2EB',
                        '#4BC0C0',
                        '#FFCE56',
                        '#FF6384',
                        '#9966FF'
                    ],
                    borderWidth: 1
                }
            ]
        });

        // Procesar datos para gráfica lineal - movimientos por mes
        const movimientosPorMes: { [key: string]: { entradas: number, salidas: number, mantenimientos: number, peticiones: number } } = {};

        // Obtener los últimos 6 meses
        const meses = [];
        const fechaActual = new Date();
        for (let i = 5; i >= 0; i--) {
            const fecha = new Date(fechaActual.getFullYear(), fechaActual.getMonth() - i, 1);
            const mesKey = `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}`;
            const mesNombre = fecha.toLocaleDateString('es-ES', { month: 'short', year: 'numeric' });
            meses.push({ key: mesKey, nombre: mesNombre });
            movimientosPorMes[mesKey] = { entradas: 0, salidas: 0, mantenimientos: 0, peticiones: 0 };
        }

        // Contar entradas por mes
        entradas.forEach((entrada: any) => {
            const fecha = new Date(entrada.fecha);
            const mesKey = `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}`;
            if (movimientosPorMes[mesKey]) {
                movimientosPorMes[mesKey].entradas++;
            }
        });

        // Contar salidas por mes
        salidas.forEach((salida: any) => {
            const fecha = new Date(salida.fecha);
            const mesKey = `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}`;
            if (movimientosPorMes[mesKey]) {
                movimientosPorMes[mesKey].salidas++;
            }
        });

        // Contar mantenimientos por mes
        mantenimientos.forEach((mantenimiento: any) => {
            const fecha = new Date(mantenimiento.fecha);
            const mesKey = `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}`;
            if (movimientosPorMes[mesKey]) {
                movimientosPorMes[mesKey].mantenimientos++;
            }
        });
        
        peticiones.forEach((peticion: PeticionType) => {
            const fecha = new Date(peticion.fecha_hora);
            const mesKey = `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}`;
            if (movimientosPorMes[mesKey]) {
                movimientosPorMes[mesKey].peticiones++;
            }
        });

        // Crear datos para gráfica lineal
        setChartDataLineal({
            labels: meses.map(m => m.nombre),
            datasets: [
                {
                    label: 'Entradas',
                    data: meses.map(m => movimientosPorMes[m.key].entradas),
                    fill: false,
                    backgroundColor: '#36A2EB',
                    borderColor: '#36A2EB',
                    tension: 0.4
                },
                {
                    label: 'Salidas',
                    data: meses.map(m => movimientosPorMes[m.key].salidas),
                    fill: false,
                    backgroundColor: '#FF6384',
                    borderColor: '#FF6384',
                    tension: 0.4
                },
                {
                    label: 'Mantenimientos',
                    data: meses.map(m => movimientosPorMes[m.key].mantenimientos),
                    fill: false,
                    backgroundColor: '#FFCE56',
                    borderColor: '#FFCE56',
                    tension: 0.4
                },
                {
                    label: 'Peticiones',
                    data: meses.map(m => movimientosPorMes[m.key].peticiones),
                    fill: false,
                    backgroundColor: '#9966FF',
                    borderColor: '#9966FF',
                    tension: 0.4
                }
            ]
        });

        // Calcular métricas de productos
        const totalProductosCount = productos.length;
        const productosConEntradasCount = Object.keys(entradasPorProducto).length;
        const productosSinEntradasCount = totalProductosCount - productosConEntradasCount;

        // Calcular productos con existencia vs sin existencia basándose en saldos calculados
        const productosConExistenciaCount = saldosProductos.filter(saldo => saldo.saldo > 0).length;
        const productosSinExistenciaCount = totalProductosCount - productosConExistenciaCount;

        setTotalProductos(totalProductosCount);
        setProductosConEntradas(productosConEntradasCount);
        setProductosSinEntradas(productosSinEntradasCount);
        setProductosConExistencia(productosConExistenciaCount);
        setProductosSinExistencia(productosSinExistenciaCount);
    };

    // Funciones para el modal
    const formatDate = (dateString?: string): string => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return isNaN(date.getTime()) ? '-' : date.toISOString().split('T')[0];
    };

    const formatDateDisplay = (dateString: string): string => {
        if (!dateString) return '-';
        
        try {
            // Si la fecha viene sin información de zona horaria, agregarla como local
            let dateToFormat = dateString;
            if (!dateString.includes('T') && !dateString.includes('Z') && !dateString.includes('+')) {
                // Si es solo fecha (YYYY-MM-DD), agregar tiempo local
                dateToFormat = dateString + 'T00:00:00';
            }
            
            const date = new Date(dateToFormat);
            
            // Verificar si la fecha es válida
            if (isNaN(date.getTime())) {
                return '-';
            }
            
            // Usar toLocaleDateString para evitar problemas de zona horaria
            const dateOptions: Intl.DateTimeFormatOptions = {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                timeZone: 'America/Mexico_City' 
            };
            
            return date.toLocaleDateString('es-ES', dateOptions);
        } catch (error) {
            console.error('Error formatting date:', error);
            return '-';
        }
    };

    const handleNavigateToInventario = (filtro?: string) => {
    // Puedes pasar parámetros de query para filtrar en la página de inventario
    const queryParams = filtro ? `?filtro=${filtro}` : '';
    navigate(`/inventario${queryParams}`);
};
    const handleActividadClick = (actividad: ActividadReciente) => {
        // Solo procesar si es entrada, salida, mantenimiento o peticion
        if (!['entrada', 'salida', 'mantenimiento', 'peticion'].includes(actividad.tipo)) {
            return;
        }

        // Extraer el folio del ID de la actividad
        const folio = actividad.id.split('-')[1];
        
        if (!folio) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Advertencia',
                detail: 'No se pudo obtener el folio del movimiento',
                life: 3000
            });
            return;
        }

        let fullData;
        let type: 'entrada' | 'salida' | 'mantenimiento' | 'peticion';

        // Buscar el movimiento completo según el tipo
        if (actividad.tipo === 'entrada') {
            fullData = entradasFull.find(e => e.folio === folio);
            type = 'entrada';
        } else if (actividad.tipo === 'salida') {
            fullData = salidasFull.find(s => s.folio === folio);
            type = 'salida';
        } else if (actividad.tipo === 'mantenimiento') {
            fullData = mantenimientosFull.find(m => m.folio === folio);
            type = 'mantenimiento';
        } else if (actividad.tipo === 'peticion') {
            fullData = peticionesFull.find(p => p.folio.toString() === folio);
            type = 'peticion';
        } else {
            // Caso por defecto para evitar el error
            toast.current?.show({
                severity: 'warn',
                summary: 'Advertencia',
                detail: 'Tipo de actividad no reconocido',
                life: 3000
            });
            return;
        }

        if (fullData) {
            setSelectedMovimiento(fullData);
            setMovimientoType(type);
            setIsModalVisible(true);
        } else {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'No se encontraron los detalles del movimiento',
                life: 3000
            });
        }
    };

    const formatDateTimeDisplay = (dateString: string): string => {
        if (!dateString) return '-';
        
        try {
            // Si la fecha viene sin información de zona horaria, agregarla como local
            let dateToFormat = dateString;
            if (!dateString.includes('T') && !dateString.includes('Z') && !dateString.includes('+')) {
                // Si es solo fecha (YYYY-MM-DD), agregar tiempo local
                dateToFormat = dateString + 'T00:00:00';
            }
            
            const date = new Date(dateToFormat);
            
            // Verificar si la fecha es válida
            if (isNaN(date.getTime())) {
                return '-';
            }
            
            // Usar toLocaleDateString y toLocaleTimeString para evitar problemas de zona horaria
            const dateOptions: Intl.DateTimeFormatOptions = {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                timeZone: 'America/Mexico_City' // Ajusta según tu zona horaria
            };
            
            const timeOptions: Intl.DateTimeFormatOptions = {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
                timeZone: 'America/Mexico_City' // Ajusta según tu zona horaria
            };
            
            const formattedDate = date.toLocaleDateString('es-ES', dateOptions);
            const formattedTime = date.toLocaleTimeString('es-ES', timeOptions);
            
            return `${formattedDate} ${formattedTime}`;
        } catch (error) {
            console.error('Error formatting date:', error);
            return '-';
        }
    };

    const getDescripcionProducto = (cve_producto: string): string => {
        const producto = productosCatalogo.find(p => p.cve_producto === cve_producto);
        return producto ? producto.descripcion : 'Producto no encontrado';
    };

    const getNombreTecnico = (id_tecnico: number): string => {
        const tecnico = usuariosCatalogo.find(u => u.id === id_tecnico);
        return tecnico ? tecnico.nombre : 'Técnico no encontrado';
    };

    const getNombreSucursal = (id: string | number): string => {
        const suc = sucursalesCatalogo.find(s => s.id === String(id));
        return suc ? suc.nombre : (id ? String(id) : '-');
    };

    const getNombreDepartamento = (cve: string): string => {
        const dept = departamentosCatalogo.find(d => d.cve === cve);
        return dept ? dept.nombre : (cve || '-');
    };

    const exportToExcel = () => {
        if (!selectedMovimiento) return;

        if (movimientoType === 'peticion') {
            const encabezado = [
                ['Folio', selectedMovimiento.folio],
                ['Fecha', formatDate(selectedMovimiento.fecha_hora)],
                ['Empleado', selectedMovimiento.empleado?.nomemp || selectedMovimiento.cveemp],
                ['Título', selectedMovimiento.titulo],
                ['Sucursal', selectedMovimiento.sucursal],
                ['Departamento', selectedMovimiento.departamento],
                ['Justificación', selectedMovimiento.justificacion]
            ];

            const ws_data: any[] = [];
            encabezado.forEach(row => ws_data.push(row));

            const worksheet = XLSX.utils.aoa_to_sheet(ws_data);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Peticion');
            const fileName = `peticion_${selectedMovimiento.folio}_${formatDate(selectedMovimiento.fecha_hora)}.xlsx`;
            XLSX.writeFile(workbook, fileName);

        } else if (movimientoType === 'mantenimiento') {
            const encabezado = [
                ['Folio', selectedMovimiento.folio],
                ['Fecha', formatDate(selectedMovimiento.fecha)],
                ['Observaciones', selectedMovimiento.observaciones || '-'],
                ['Producto Principal', getDescripcionProducto(selectedMovimiento.cve_producto_principal)],
                ['Técnico', getNombreTecnico(selectedMovimiento.id_tecnico)]
            ];

            const detalle = (selectedMovimiento.mantenimientoDetalle || []).map((detalle: any) => [
                detalle.cve_producto,
                getDescripcionProducto(detalle.cve_producto),
                detalle.cantidad,
                detalle.observaciones || '-'
            ]);

            const ws_data: any[] = [];
            encabezado.forEach(row => ws_data.push(row));
            ws_data.push([]);
            ws_data.push(['Detalle:']);
            ws_data.push(['Clave', 'Descripción', 'Cantidad', 'Observación']);
            detalle.forEach((row: any) => ws_data.push(row));

            const worksheet = XLSX.utils.aoa_to_sheet(ws_data);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Mantenimiento');
            const fileName = `mantenimiento_${selectedMovimiento.folio}_${formatDate(selectedMovimiento.fecha)}.xlsx`;
            XLSX.writeFile(workbook, fileName);

        } else if (movimientoType === 'salida') {
            const detalle = selectedMovimiento.salidaDetalle || [];

            const excelData = detalle.map((detalle: any) => ({
                Clave: detalle.cve_producto,
                Descripción: getDescripcionProducto(detalle.cve_producto),
                Cantidad: detalle.cantidad,
                Sucursal: getNombreSucursal(detalle.id_sucursal_destino) || '-',
                Departamento: getNombreDepartamento(detalle.cve_departamento_destino) || '-',
                Usuario: detalle.nombre_usuario || '-'
            }));

            const worksheet = XLSX.utils.json_to_sheet(excelData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Datos');
            const fileName = `detalles_salida_${selectedMovimiento.folio}_${formatDate(selectedMovimiento.fecha)}.xlsx`;
            XLSX.writeFile(workbook, fileName);

        } else {
            // Entrada
            const detalle = selectedMovimiento.entradaDetalle || [];

            const excelData = detalle.map((detalle: any) => ({
                Clave: detalle.cve_producto,
                Descripción: getDescripcionProducto(detalle.cve_producto),
                Cantidad: detalle.cantidad
            }));

            const worksheet = XLSX.utils.json_to_sheet(excelData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Datos');
            const fileName = `detalles_entrada_${selectedMovimiento.folio}_${formatDate(selectedMovimiento.fecha)}.xlsx`;
            XLSX.writeFile(workbook, fileName);
        }
    };

   const exportToPdf = () => {
    if (!selectedMovimiento) return;

    const doc = new jsPDF('l', 'pt', 'a4');
    const img = new Image();
    img.src = '/dist/img/bbtt2.png';
    img.onload = function () {
        doc.addImage(img, 'PNG', 40, 20, 65, 65);

        doc.setProperties({
            title: 'Reporte de Dashboard',
            subject: `Detalles de ${movimientoType.charAt(0).toUpperCase() + movimientoType.slice(1)}`,
            author: 'Sistema de Inventarios',
            keywords: 'dashboard, reporte, pdf',
            creator: 'Sistema de Inventarios'
        });

        doc.setFontSize(18);
        doc.setTextColor(40);
        doc.text(`Detalles de ${movimientoType.charAt(0).toUpperCase() + movimientoType.slice(1)}`, 150, 40);
        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text(`Generado el: ${new Date().toLocaleDateString('es-ES')}`, 150, 60);

        let y = 100;

        if (movimientoType === 'peticion') {
            // AGREGADO: Sección para peticiones
            doc.setFontSize(12);
            doc.text(`Folio:`, 40, y);
            doc.setFont('helvetica', 'bold');
            doc.text(`${selectedMovimiento.folio}`, 75, y);
            doc.setFont('helvetica', 'normal');
            y += 18;
            doc.text(`Fecha:`, 40, y);
            doc.setFont('helvetica', 'bold');
            doc.text(`${formatDate(selectedMovimiento.fecha_hora)}`, 85, y);
            doc.setFont('helvetica', 'normal');
            y += 18;
            doc.text(`Empleado:`, 40, y);
            doc.setFont('helvetica', 'bold');
            doc.text(`${selectedMovimiento.empleado?.nomemp || selectedMovimiento.cveemp}`, 100, y);
            doc.setFont('helvetica', 'normal');
            y += 18;
            doc.text(`Título:`, 40, y);
            doc.setFont('helvetica', 'bold');
            // Para títulos largos, usar splitTextToSize
            const tituloLines = doc.splitTextToSize(selectedMovimiento.titulo || '-', 500);
            doc.text(tituloLines, 80, y);
            doc.setFont('helvetica', 'normal');
            y += (tituloLines.length * 12) + 6;
            doc.text(`Sucursal:`, 40, y);
            doc.setFont('helvetica', 'bold');
            doc.text(`${selectedMovimiento.sucursal}`, 95, y);
            doc.setFont('helvetica', 'normal');
            y += 18;
            doc.text(`Departamento:`, 40, y);
            doc.setFont('helvetica', 'bold');
            doc.text(`${selectedMovimiento.departamento}`, 130, y);
            doc.setFont('helvetica', 'normal');
            y += 18;
            doc.text(`Justificación:`, 40, y);
            doc.setFont('helvetica', 'bold');
            // Para justificaciones largas, usar splitTextToSize
            const justificacionLines = doc.splitTextToSize(selectedMovimiento.justificacion || '-', 500);
            doc.text(justificacionLines, 130, y);
            doc.setFont('helvetica', 'normal');
            
            // Agregar firma
            y += (justificacionLines.length * 12) + 40;
            doc.setFontSize(12);
            doc.setDrawColor(100);
            doc.line(300, y, 550, y);
            doc.text('Firma de conformidad', 425, y + 20, { align: 'center' });
            
            doc.save(`peticion_${selectedMovimiento.folio}_${formatDate(selectedMovimiento.fecha_hora)}.pdf`);

        } else if (movimientoType === 'mantenimiento') {
            doc.setFontSize(12);
            doc.text(`Folio:`, 40, y);
            doc.setFont('helvetica', 'bold');
            doc.text(`${selectedMovimiento.folio}`, 75, y);
            doc.setFont('helvetica', 'normal');
            y += 18;
            doc.text(`Fecha:`, 40, y);
            doc.setFont('helvetica', 'bold');
            doc.text(`${formatDate(selectedMovimiento.fecha)}`, 85, y);
            doc.setFont('helvetica', 'normal');
            y += 18;
            doc.text(`Observaciones:`, 40, y);
            doc.setFont('helvetica', 'bold');
            // MODIFICADO: Usar splitTextToSize para observaciones largas
            const observacionesLines = doc.splitTextToSize(selectedMovimiento.observaciones || '-', 500);
            doc.text(observacionesLines, 130, y);
            doc.setFont('helvetica', 'normal');
            y += (observacionesLines.length * 12) + 6;
            doc.text(`Producto Principal:`, 40, y);
            doc.setFont('helvetica', 'bold');
            // MODIFICADO: Usar splitTextToSize para descripciones largas
            const productoLines = doc.splitTextToSize(getDescripcionProducto(selectedMovimiento.cve_producto_principal), 400);
            doc.text(productoLines, 145, y);
            doc.setFont('helvetica', 'normal');
            y += (productoLines.length * 12) + 6;
            doc.text(`Técnico:`, 40, y);
            doc.setFont('helvetica', 'bold');
            doc.text(`${getNombreTecnico(selectedMovimiento.id_tecnico)}`, 90, y);
            doc.setFont('helvetica', 'normal');
            y += 18;

            const detalle = selectedMovimiento.mantenimientoDetalle || [];
            const pdfData = detalle.map((detalle: any) => [
                detalle.cve_producto,
                getDescripcionProducto(detalle.cve_producto),
                detalle.cantidad,
                detalle.observaciones || '-'
            ]);

            const autoTableResult = autoTable(doc, {
                head: [['Clave', 'Descripción', 'Cantidad', 'Observación']],
                body: pdfData,
                startY: y + 20,
                margin: { horizontal: 40 },
                styles: {
                    fontSize: 8,
                    cellPadding: 3,
                    overflow: 'linebreak',
                    valign: 'middle'
                },
                headStyles: {
                    fillColor: [41, 128, 185],
                    textColor: 255,
                    fontStyle: 'bold',
                    halign: 'center',
                    fontSize: 9
                },
                bodyStyles: {
                    halign: 'center',
                    valign: 'middle'
                },
                columnStyles: {
                    0: { cellWidth: 120, halign: 'center' },
                    1: { cellWidth: 220, halign: 'left', overflow: 'linebreak' }, // MODIFICADO: Ajuste para texto largo
                    2: { cellWidth: 80, halign: 'center' },
                    3: { cellWidth: 210, halign: 'left', overflow: 'linebreak' }
                },
                didDrawPage: function (data: any) {
                    const pageCount = doc.getNumberOfPages();
                    const currentPage = data.pageNumber;
                    doc.setFontSize(10);
                    doc.setTextColor(150);
                    doc.text(`Página ${currentPage} de ${pageCount}`, data.settings.margin.left, doc.internal.pageSize.height - 20);
                }
            });

            let finalY = (autoTableResult && autoTableResult.finalY) || (y + 290);
            doc.setFontSize(12);
            doc.setDrawColor(100);
            doc.line(300, finalY + 40, 550, finalY + 40);
            doc.text('Firma de conformidad', 425, finalY + 60, { align: 'center' });
            doc.save(`mantenimiento_${selectedMovimiento.folio}_${formatDate(selectedMovimiento.fecha)}.pdf`);

        } else if (movimientoType === 'salida') {
            doc.setFontSize(12);
            doc.text(`Folio:`, 40, y);
            doc.setFont('helvetica', 'bold');
            doc.text(`${selectedMovimiento.folio}`, 75, y);
            doc.setFont('helvetica', 'normal');
            y += 18;
            doc.text(`Fecha:`, 40, y);
            doc.setFont('helvetica', 'bold');
            doc.text(`${formatDate(selectedMovimiento.fecha)}`, 85, y);
            doc.setFont('helvetica', 'normal');
            y += 18;
            doc.text(`Observaciones:`, 40, y);
            doc.setFont('helvetica', 'bold');
            // MODIFICADO: Usar splitTextToSize para observaciones largas
            const observacionesLines = doc.splitTextToSize(selectedMovimiento.observaciones || '-', 500);
            doc.text(observacionesLines, 130, y);
            doc.setFont('helvetica', 'normal');
            y += (observacionesLines.length * 12) + 6;

            const tableData = selectedMovimiento.salidaDetalle || [];
            const pdfData = tableData.map((detalle: any) => [
                detalle.cve_producto,
                getDescripcionProducto(detalle.cve_producto),
                detalle.cantidad,
                getNombreSucursal(detalle.id_sucursal_destino) || '-',
                getNombreDepartamento(detalle.cve_departamento_destino) || '-',
                detalle.nombre_usuario || '-'
            ]);

            const autoTableResult = autoTable(doc, {
                head: [['Clave', 'Descripción', 'Cant.', 'Sucursal', 'Departamento', 'Usuario']],
                body: pdfData,
                startY: y + 20,
                margin: { horizontal: 40 },
                styles: {
                    fontSize: 7,
                    cellPadding: 2,
                    overflow: 'linebreak',
                    valign: 'middle'
                },
                headStyles: {
                    fillColor: [41, 128, 185],
                    textColor: 255,
                    fontStyle: 'bold',
                    halign: 'center',
                    fontSize: 8
                },
                bodyStyles: {
                    halign: 'center',
                    valign: 'middle'
                },
                columnStyles: {
                    0: { cellWidth: 70, halign: 'center' },
                    1: { cellWidth: 180, halign: 'left', overflow: 'linebreak' }, // MODIFICADO: Ajuste para texto largo
                    2: { cellWidth: 40, halign: 'center' },
                    3: { cellWidth: 90, halign: 'center', overflow: 'linebreak' },
                    4: { cellWidth: 90, halign: 'center', overflow: 'linebreak' },
                    5: { cellWidth: 90, halign: 'center', overflow: 'linebreak' }
                },
                didDrawPage: function (data: any) {
                    const pageCount = doc.getNumberOfPages();
                    const currentPage = data.pageNumber;
                    doc.setFontSize(10);
                    doc.setTextColor(150);
                    doc.text(`Página ${currentPage} de ${pageCount}`, data.settings.margin.left, doc.internal.pageSize.height - 20);
                }
            });

            // AGREGADO: Firma de conformidad para salidas
            let finalY = (autoTableResult && autoTableResult.finalY) || (y + 290);
            doc.setFontSize(12);
            doc.setDrawColor(100);
            doc.line(300, finalY + 40, 550, finalY + 40);
            doc.text('Firma de conformidad', 425, finalY + 60, { align: 'center' });
            
            doc.save(`detalles_salida_${selectedMovimiento.folio}_${formatDate(selectedMovimiento.fecha)}.pdf`);
        } else {
            // Entrada
            doc.setFontSize(12);
            doc.text(`Folio:`, 40, y);
            doc.setFont('helvetica', 'bold');
            doc.text(`${selectedMovimiento.folio}`, 75, y);
            doc.setFont('helvetica', 'normal');
            y += 18;
            doc.text(`Fecha:`, 40, y);
            doc.setFont('helvetica', 'bold');
            doc.text(`${formatDate(selectedMovimiento.fecha)}`, 85, y);
            doc.setFont('helvetica', 'normal');
            y += 18;
            doc.text(`Referencia:`, 40, y);
            doc.setFont('helvetica', 'bold');
            doc.text(`${selectedMovimiento.referencia || '-'}`, 106, y);
            doc.setFont('helvetica', 'normal');
            y += 18;
            doc.text(`Observaciones:`, 40, y);
            doc.setFont('helvetica', 'bold');
            // MODIFICADO: Usar splitTextToSize para observaciones largas
            const observacionesLines = doc.splitTextToSize(selectedMovimiento.observaciones || '-', 500);
            doc.text(observacionesLines, 130, y);
            doc.setFont('helvetica', 'normal');
            y += (observacionesLines.length * 12) + 6;

            const tableData = selectedMovimiento.entradaDetalle || [];
            const pdfData = tableData.map((detalle: any) => [
                detalle.cve_producto,
                getDescripcionProducto(detalle.cve_producto),
                detalle.cantidad
            ]);

            const autoTableResult = autoTable(doc, {
                head: [['Clave', 'Descripción', 'Cantidad']],
                body: pdfData,
                startY: y + 20,
                margin: { horizontal: 40 },
                styles: {
                    fontSize: 8,
                    cellPadding: 3,
                    overflow: 'linebreak',
                    valign: 'middle'
                },
                headStyles: {
                    fillColor: [41, 128, 185],
                    textColor: 255,
                    fontStyle: 'bold',
                    halign: 'center',
                    fontSize: 9
                },
                bodyStyles: {
                    halign: 'center',
                    valign: 'middle'
                },
                columnStyles: {
                    0: { cellWidth: 150, halign: 'center' },
                    1: { cellWidth: 350, halign: 'left', overflow: 'linebreak' }, // MODIFICADO: Más espacio para descripción
                    2: { cellWidth: 100, halign: 'center' }
                },
                didDrawPage: function (data: any) {
                    const pageCount = doc.getNumberOfPages();
                    const currentPage = data.pageNumber;
                    doc.setFontSize(10);
                    doc.setTextColor(150);
                    doc.text(`Página ${currentPage} de ${pageCount}`, data.settings.margin.left, doc.internal.pageSize.height - 20);
                }
            });

            // AGREGADO: Firma de conformidad para entradas también
            let finalY = (autoTableResult && autoTableResult.finalY) || (y + 290);
            doc.setFontSize(12);
            doc.setDrawColor(100);
            doc.line(300, finalY + 40, 550, finalY + 40);
            doc.text('Firma de conformidad', 425, finalY + 60, { align: 'center' });

            doc.save(`detalles_entrada_${selectedMovimiento.folio}_${formatDate(selectedMovimiento.fecha)}.pdf`);
        }
    };
};

    const getActividadBadge = (tipo: string) => {
        const config = {
            entrada: { severity: 'success' as const, icon: 'fas fa-arrow-up', color: '#28a745' },
            salida: { severity: 'danger' as const, icon: 'fas fa-arrow-down', color: '#dc3545' },
            mantenimiento: { severity: 'warning' as const, icon: 'fas fa-wrench', color: '#ffc107' },
            peticion: { severity: 'info' as const, icon: 'fas fa-file-alt', color: '#17a2b8' },
            usuario: { severity: 'info' as const, icon: 'fas fa-user', color: '#17a2b8' },
            producto: { severity: 'secondary' as const, icon: 'fas fa-cube', color: '#6c757d' },
            sucursal: { severity: 'primary' as const, icon: 'fas fa-building', color: '#007bff' },
            departamento: { severity: 'info' as const, icon: 'fas fa-sitemap', color: '#17a2b8' },
            proveedor: { severity: 'secondary' as const, icon: 'fas fa-address-book', color: '#6c757d' },
            marca: { severity: 'primary' as const, icon: 'fas fa-plus-square', color: '#007bff' },
            linea: { severity: 'info' as const, icon: 'fas fa-link', color: '#17a2b8' },
            sublinea: { severity: 'secondary' as const, icon: 'fas fa-bars', color: '#6c757d' },
            unidad: { severity: 'primary' as const, icon: 'fas fa-tags', color: '#007bff' }
        };
        return config[tipo as keyof typeof config] || config.producto;
    };

    if (loading) {
        return (
            <>
                <Toast ref={toast} />
                <Navbar />
                <Sidebar />
                <div className="content-wrapper" style={{
                    backgroundImage: 'url(/dist/img/FondoLogin.png)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    backgroundAttachment: 'fixed',
                    minHeight: '100vh',
                    position: 'relative'
                }}>
                    {/* Overlay más sutil */}
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(255, 255, 255, 0.4)', // Menos opaco
                        zIndex: 1
                    }}></div>
                    
                    <div style={{ position: 'relative', zIndex: 2 }}>
                        <div className="content-header">
                            <div className="container-fluid">
                                <div className="row mb-2">
                                    <div className="col-sm-6">
                                        <h1 className="m-0">Dashboard BBT</h1>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <section className="content">
                            <div className="container-fluid">
                                <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                                    <div className="text-center">
                                        <i className="fas fa-spinner fa-spin fa-3x text-primary mb-3"></i>
                                        <h4>Cargando dashboard...</h4>
                                    </div>
                                </div>
                            </div>
                        </section>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <Toast ref={toast} />
            <Navbar />
            <Sidebar />
            <div className="content-wrapper" style={{
                backgroundImage: 'url(/dist/img/FondoLogin.png)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundAttachment: 'fixed',
                minHeight: '100vh',
                position: 'relative'
            }}>
                {/* Overlay más sutil */}
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(255, 255, 255, 0.4)', // Menos opaco
                    zIndex: 1
                }}></div>

                <div style={{ position: 'relative', zIndex: 2 }}>
                    <div className="content-header">
                        <div className="container-fluid">
                            <div className="row mb-2">
                                <div className="col-sm-6">
                                    <h1 className="m-0">Dashboard BBT</h1>
                                </div>
                                <div className="col-sm-6">
                                    <ol className="breadcrumb float-sm-right">
                                        <li className="breadcrumb-item active">Dashboard BBT</li>
                                    </ol>
                                </div>
                            </div>
                        </div>
                    </div>

                    <section className="content">
                        <div className="container-fluid">
                            {/* Primera fila - Métricas principales */}
                            <div className="row mb-4">
                                {/* Métricas de productos combinadas */}
                                <div className="col-lg-6 col-md-12">
                                    <Card title="Resumen de Productos" className="h-100" style={{ 
                                        height: '65%',
                                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                        backdropFilter: 'blur(10px)',
                                        border: '1px solid rgba(255, 255, 255, 0.2)',
                                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
                                    }}>
                                        <div className="text-center" style={{ padding: '0.5rem 0' }}>
                                            {/* Productos totales - Clickeable */}
                                            <div 
                                                className="clickeable-metric"
                                                onClick={() => handleNavigateToInventario('todos')}
                                                style={{
                                                    cursor: 'pointer',
                                                    padding: '0.5rem',
                                                    borderRadius: '8px',
                                                    transition: 'all 0.2s ease',
                                                    marginBottom: '1rem'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.backgroundColor = 'rgba(0, 123, 255, 0.1)';
                                                    e.currentTarget.style.transform = 'scale(1.02)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.backgroundColor = 'transparent';
                                                    e.currentTarget.style.transform = 'scale(1)';
                                                }}
                                            >
                                                <i className="fas fa-cubes fa-lg text-primary mb-1"></i>
                                                <h4 className="text-primary mb-1">{totalProductos}</h4>
                                                <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
                                                    Productos totales en catálogo
                                                    <br />
                                                    <small className="text-primary" style={{ fontSize: '0.7rem' }}>
                                                        <i className="fas fa-mouse-pointer mr-1"></i>
                                                        Clic para ver todos
                                                    </small>
                                                </p>
                                            </div>
                                            
                                            {/* Existencias */}
                                            <div className="row mb-2">
                                                <div className="col-6">
                                                    <div 
                                                        className="border-right clickeable-metric"
                                                        onClick={() => handleNavigateToInventario('con-existencia')}
                                                        style={{
                                                            cursor: 'pointer',
                                                            padding: '0.5rem',
                                                            borderRadius: '8px',
                                                            transition: 'all 0.2s ease'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.backgroundColor = 'rgba(40, 167, 69, 0.1)';
                                                            e.currentTarget.style.transform = 'scale(1.05)';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.backgroundColor = 'transparent';
                                                            e.currentTarget.style.transform = 'scale(1)';
                                                        }}
                                                    >
                                                        <i className="fas fa-check-circle text-success mb-1" style={{ fontSize: '1.1rem' }}></i>
                                                        <h6 className="text-success mb-0">{productosConExistencia}</h6>
                                                        <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                            Con Existencia
                                                            <br />
                                                            <span className="text-success" style={{ fontSize: '0.65rem' }}>
                                                                <i className="fas fa-eye mr-1"></i>Ver
                                                            </span>
                                                        </small>
                                                    </div>
                                                </div>
                                                <div className="col-6">
                                                    <div 
                                                        className="clickeable-metric"
                                                        onClick={() => handleNavigateToInventario('sin-existencia')}
                                                        style={{
                                                            cursor: 'pointer',
                                                            padding: '0.5rem',
                                                            borderRadius: '8px',
                                                            transition: 'all 0.2s ease'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.backgroundColor = 'rgba(220, 53, 69, 0.1)';
                                                            e.currentTarget.style.transform = 'scale(1.05)';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.backgroundColor = 'transparent';
                                                            e.currentTarget.style.transform = 'scale(1)';
                                                        }}
                                                    >
                                                        <i className="fas fa-times-circle text-danger mb-1" style={{ fontSize: '1.1rem' }}></i>
                                                        <h6 className="text-danger mb-0">{productosSinExistencia}</h6>
                                                        <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                            Sin Existencia
                                                            <br />
                                                            <span className="text-danger" style={{ fontSize: '0.65rem' }}>
                                                                <i className="fas fa-eye mr-1"></i>Ver
                                                            </span>
                                                        </small>
                                                    </div>
                                                </div>
                                            </div>

                                            <hr className="my-1" style={{ margin: '0.25rem 0' }} />

                                            {/* Entradas */}
                                            <div className="row">
                                                <div className="col-6">
                                                    <div 
                                                        className="border-right clickeable-metric"
                                                        onClick={() => handleNavigateToInventario('con-entradas')}
                                                        style={{
                                                            cursor: 'pointer',
                                                            padding: '0.5rem',
                                                            borderRadius: '8px',
                                                            transition: 'all 0.2s ease'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.backgroundColor = 'rgba(23, 162, 184, 0.1)';
                                                            e.currentTarget.style.transform = 'scale(1.05)';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.backgroundColor = 'transparent';
                                                            e.currentTarget.style.transform = 'scale(1)';
                                                        }}
                                                    >
                                                        <i className="fas fa-arrow-up text-info mb-1" style={{ fontSize: '1.1rem' }}></i>
                                                        <h6 className="text-info mb-0">{productosConEntradas}</h6>
                                                        <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                            Con Entradas
                                                            <br />
                                                            <span className="text-info" style={{ fontSize: '0.65rem' }}>
                                                                <i className="fas fa-eye mr-1"></i>Ver
                                                            </span>
                                                        </small>
                                                    </div>
                                                </div>
                                                <div className="col-6">
                                                    <div 
                                                        className="clickeable-metric"
                                                        onClick={() => handleNavigateToInventario('sin-entradas')}
                                                        style={{
                                                            cursor: 'pointer',
                                                            padding: '0.5rem',
                                                            borderRadius: '8px',
                                                            transition: 'all 0.2s ease'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.backgroundColor = 'rgba(255, 193, 7, 0.1)';
                                                            e.currentTarget.style.transform = 'scale(1.05)';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.backgroundColor = 'transparent';
                                                            e.currentTarget.style.transform = 'scale(1)';
                                                        }}
                                                    >
                                                        <i className="fas fa-minus-circle text-warning mb-1" style={{ fontSize: '1.1rem' }}></i>
                                                        <h6 className="text-warning mb-0">{productosSinEntradas}</h6>
                                                        <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                            Sin Entradas
                                                            <br />
                                                            <span className="text-warning" style={{ fontSize: '0.65rem' }}>
                                                                <i className="fas fa-eye mr-1"></i>Ver
                                                            </span>
                                                        </small>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Card>
                                </div>

                                {/* Gráfico Lineal - Expandido */}
                                <div className="col-lg-6 col-md-12">
                                    <div className="card h-100" style={{
                                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                        backdropFilter: 'blur(10px)',
                                        border: '1px solid rgba(255, 255, 255, 0.2)',
                                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
                                    }}>
                                        <div className="card-header">
                                            <h3 className="card-title">
                                                <i className="fas fa-chart-line mr-1"></i>
                                                Movimientos de Inventario (Últimos 6 Meses)
                                            </h3>
                                        </div>
                                        <div className="card-body">
                                            <div style={{ height: '300px' }}>
                                                <Chart type="line" data={chartDataLineal} options={lineOptions} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Segunda fila - Top productos combinados */}
                            <div className="row mb-4">
                                <div className="col-12">
                                    <Card title="Top 5 Productos" className="h-100" style={{
                                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                        backdropFilter: 'blur(10px)',
                                        border: '1px solid rgba(255, 255, 255, 0.2)',
                                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
                                    }}>
                                        <div className="row mb-3">
                                            <div className="col-7">
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <h5 className="mb-0 mr-2 flex-grow-1">
                                                        <i className={`fas ${tipoGrafica === 'entradas' ? 'fa-arrow-up text-success' : 'fa-arrow-down text-danger'} mr-2`}></i>
                                                        {tipoGrafica === 'entradas' ? 'Productos con Más Entradas' : 'Productos con Más Salidas'}
                                                    </h5>
                                                    
                                                </div>
                                                <div>  
                                                    <p></p>
                                                     </div>
                                                <div className="form-group mb-0 ml-1">
                                                        <select 
                                                            className="form-control form-control-sm"
                                                            value={tipoGrafica}
                                                            onChange={(e) => setTipoGrafica(e.target.value as 'entradas' | 'salidas')}
                                                            style={{ width: '200px' }}
                                                        >
                                                            <option value="entradas">Entradas</option>
                                                            <option value="salidas">Salidas</option>
                                                        </select>
                                                    </div>
                                            </div>
                                        </div>
                                        
                                        <div className="row">
                                            <div className="col-12">
                                                <div style={{ height: '200px' }}>
                                                    <Chart
                                                        type="bar"
                                                        data={tipoGrafica === 'entradas' ? chartDataEntradas : chartDataSalidas}
                                                        options={{
                                                            responsive: true,
                                                            maintainAspectRatio: false,
                                                            plugins: {
                                                                legend: {
                                                                    display: false
                                                                },
                                                                tooltip: {
                                                                    callbacks: {
                                                                        label: function(context: any) {
                                                                            return `${tipoGrafica === 'entradas' ? 'Entradas' : 'Salidas'}: ${context.parsed.y}`;
                                                                        }
                                                                    }
                                                                }
                                                            },
                                                            scales: {
                                                                x: {
                                                                    ticks: {
                                                                        maxRotation: 45,
                                                                        minRotation: 0
                                                                    }
                                                                },
                                                                y: {
                                                                    beginAtZero: true,
                                                                    ticks: {
                                                                        stepSize: 1
                                                                    }
                                                                }
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </Card>
                                </div>
                            </div>

                            {/* Actividad Reciente - Ancho completo con 12 tarjetas */}
                            <div className="row mb-4">
                                <div className="col-12">
                                    <Card title="Actividad Reciente" className="h-100" style={{
                                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                        backdropFilter: 'blur(10px)',
                                        border: '1px solid rgba(255, 255, 255, 0.2)',
                                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
                                    }}>
                                        <div>
                                            {actividadReciente.filter(actividad => ['entrada', 'salida', 'mantenimiento', 'peticion'].includes(actividad.tipo)).length === 0 ? (
                                                <div className="text-center text-muted py-4">
                                                    <i className="fas fa-clock fa-2x mb-2"></i>
                                                    <p>No hay actividad reciente</p>
                                                </div>
                                            ) : (
                                                <div className="row">
                                                    {actividadReciente
                                                        .filter(actividad => ['entrada', 'salida', 'mantenimiento', 'peticion'].includes(actividad.tipo))
                                                        .slice(0, 12) // Cambiado de 6 a 12
                                                        .map((actividad, index) => {
                                                            const config = getActividadBadge(actividad.tipo);
                                                            return (
                                                                <div key={actividad.id} className="col-lg-3 col-md-4 col-sm-6 mb-3"> {/* Cambiado de col-lg-4 a col-lg-3 para mostrar 4 por fila */}
                                                                    <div 
                                                                        className="card h-100" 
                                                                        style={{ 
                                                                            backgroundColor: 'rgba(248, 249, 250, 0.8)',
                                                                            border: '1px solid rgba(0, 0, 0, 0.1)',
                                                                            fontSize: '0.85rem',
                                                                            cursor: 'pointer',
                                                                            transition: 'all 0.2s ease-in-out'
                                                                        }}
                                                                        onClick={() => handleActividadClick(actividad)}
                                                                        onMouseEnter={(e) => {
                                                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                                                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                                                                        }}
                                                                        onMouseLeave={(e) => {
                                                                            e.currentTarget.style.transform = 'translateY(0)';
                                                                            e.currentTarget.style.boxShadow = 'none';
                                                                        }}
                                                                    >
                                                                        <div className="card-body p-3">
                                                                            <div className="d-flex align-items-start">
                                                                                <div className="mr-3 mt-1">
                                                                                    <i 
                                                                                        className={config.icon} 
                                                                                        style={{ 
                                                                                            color: config.color, 
                                                                                            fontSize: '1.2rem',
                                                                                            width: '20px'
                                                                                        }}
                                                                                    ></i>
                                                                                </div>
                                                                                <div className="flex-grow-1">
                                                                                    <div className="font-weight-bold mb-2" style={{ fontSize: '0.9rem' }}>
                                                                                        {actividad.descripcion}
                                                                                    </div>
                                                                                    {actividad.detalle && (
                                                                                        <div className="text-muted mb-2" style={{ fontSize: '0.8rem' }}>
                                                                                            <i className="fas fa-info-circle mr-1"></i>
                                                                                            {actividad.detalle}
                                                                                        </div>
                                                                                    )}
                                                                                    {actividad.usuario && (
                                                                                        <div className="text-info mb-2" style={{ fontSize: '0.8rem' }}>
                                                                                            <i className="fas fa-user mr-1"></i>
                                                                                            {actividad.usuario}
                                                                                        </div>
                                                                                    )}
                                                                                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                                                        <i className="fas fa-clock mr-1"></i>
                                                                                        {formatDateTimeDisplay(actividad.fecha)}
                                                                                    </div>
                                                                                    <div className="text-primary mt-2" style={{ fontSize: '0.7rem' }}>
                                                                                        <i className="fas fa-eye mr-1"></i>
                                                                                        Clic para ver detalles
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                </div>
                                            )}
                                        </div>
                                    </Card>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </div>

            {/* Modal de detalles */}
            <Dialog
                header={
                    movimientoType === 'entrada' ? 'Detalles de Entrada' :
                    movimientoType === 'salida' ? 'Detalles de Salida' :
                    movimientoType === 'mantenimiento' ? 'Detalles de Mantenimiento' :
                    'Detalles de Petición'
                }
                visible={isModalVisible}
                style={{ width: '90%' }}
                onHide={() => setIsModalVisible(false)}
            >
                {selectedMovimiento && (
                    <>
                        <h5>
                            {movimientoType === 'entrada' ? 'Encabezado de Entrada' :
                                movimientoType === 'salida' ? 'Encabezado de Salida' :
                                movimientoType === 'mantenimiento' ? 'Encabezado de Mantenimiento' :
                                    'Encabezado de Petición'}
                        </h5>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '-2rem' }}>
                            <button
                                onClick={exportToExcel}
                                className="btn btn-sm btn-success mr-2"
                            >
                                <i className="pi pi-file-excel"></i>
                            </button>
                            <button
                                onClick={exportToPdf}
                                className="btn btn-sm btn-danger"
                            >
                                <i className="pi pi-file-pdf"></i>
                            </button>
                        </div>
                        <table className="table table-bordered">
                            <tbody>
                                <tr><th>Folio</th><td>{selectedMovimiento.folio}</td></tr>
<tr><th>Fecha</th><td>{formatDate(movimientoType === 'peticion' ? selectedMovimiento.fecha_hora : selectedMovimiento.fecha)}</td></tr>                                {movimientoType === 'entrada' && (
                                    <tr><th>Referencia</th><td>{selectedMovimiento.referencia || '-'}</td></tr>
                                )}
                                <tr><th>Observaciones</th><td>{selectedMovimiento.observaciones || '-'}</td></tr>
                                {movimientoType === 'salida' && (() => {
                                    const detalles = Array.isArray(selectedMovimiento.salidaDetalle) ? selectedMovimiento.salidaDetalle : [];
                                    const sucursales = detalles.length > 0
                                        ? Array.from(new Set(detalles.map((d: any) => getNombreSucursal(d.id_sucursal_destino)))).join(', ')
                                        : '-';
                                    const departamentos = detalles.length > 0
                                        ? Array.from(new Set(detalles.map((d: any) => getNombreDepartamento(d.cve_departamento_destino)))).join(', ')
                                        : '-';
                                    return [
                                        <tr key="sucursal">
                                            <th>Sucursal Destino</th>
                                            <td>{sucursales}</td>
                                        </tr>,
                                        <tr key="departamento">
                                            <th>Departamento Destino</th>
                                            <td>{departamentos}</td>
                                        </tr>
                                    ];
                                })()}
                                {movimientoType === 'mantenimiento' && (
                                    <>
                                        <tr><th>Producto Principal</th><td>{getDescripcionProducto(selectedMovimiento.cve_producto_principal)}</td></tr>
                                        <tr><th>Técnico</th><td>{getNombreTecnico(selectedMovimiento.id_tecnico)}</td></tr>
                                    </>
                                )}
                                {movimientoType === 'peticion' && (
                                    <>
                                        <tr><th>Empleado</th><td>{selectedMovimiento.empleado?.nomemp || selectedMovimiento.cveemp}</td></tr>
                                        <tr><th>Título</th><td>{selectedMovimiento.titulo}</td></tr>
                                        <tr><th>Sucursal</th><td>{selectedMovimiento.sucursal}</td></tr>
                                        <tr><th>Departamento</th><td>{selectedMovimiento.departamento}</td></tr>
                                        <tr><th>Justificación</th><td>{selectedMovimiento.justificacion}</td></tr>
                                    </>
                                )}
                            </tbody>
                        </table>

                        {movimientoType !== 'peticion' && (
                            <>
                                <h5>Detalle</h5>
                                <div style={{ maxHeight: '400px', overflowY: 'auto', overflowX: 'auto' }}>
                                    <table className="table table-bordered table-striped table-hover table-sm" style={{ marginBottom: 0, fontSize: '0.875rem' }}>
                                        <thead className="thead-dark" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                                            <tr>
                                                <th style={{ minWidth: '100px', textAlign: 'center', verticalAlign: 'middle' }}>Clave</th>
                                                <th style={{ minWidth: '200px', textAlign: 'center', verticalAlign: 'middle' }}>Descripción</th>
                                                <th style={{ minWidth: '60px', width: '60px', textAlign: 'center', verticalAlign: 'middle' }}>Cant.</th>
                                                {movimientoType === 'mantenimiento' && (
                                                    <th style={{ minWidth: '150px', textAlign: 'center', verticalAlign: 'middle' }}>Observación</th>
                                                )}
                                                {movimientoType === 'salida' && (
                                                    <>
                                                        <th style={{ minWidth: '90px', width: '90px', textAlign: 'center', verticalAlign: 'middle' }}>Sucursal</th>
                                                        <th style={{ minWidth: '130px', textAlign: 'center', verticalAlign: 'middle' }}>Departamento</th>
                                                        <th style={{ minWidth: '120px', textAlign: 'center', verticalAlign: 'middle' }}>Usuario</th>
                                                    </>
                                                )}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(() => {
                                                let detalleArray = [];
                                                if (movimientoType === 'entrada') {
                                                    detalleArray = selectedMovimiento.entradaDetalle || [];
                                                } else if (movimientoType === 'salida') {
                                                    detalleArray = selectedMovimiento.salidaDetalle || [];
                                                } else if (movimientoType === 'mantenimiento') {
                                                    detalleArray = selectedMovimiento.mantenimientoDetalle || [];
                                                }
                                                
                                                if (detalleArray.length === 0) {
                                                    const colSpan = movimientoType === 'entrada' ? 3 : 
                                                                  movimientoType === 'salida' ? 6 : 4;
                                                    return (
                                                        <tr>
                                                            <td colSpan={colSpan} style={{ textAlign: 'center', padding: '20px', fontStyle: 'italic', color: '#6c757d' }}>
                                                                No hay detalles disponibles
                                                            </td>
                                                        </tr>
                                                    );
                                                }
                                                
                                                return detalleArray.map((detalle: any, index: number) => (
                                                    <tr key={index} style={{ transition: 'background-color 0.2s' }}>
                                                        <td style={{ 
                                                            textAlign: 'center', 
                                                            verticalAlign: 'middle',
                                                            fontWeight: '500',
                                                            backgroundColor: '#f8f9fa'
                                                        }}>
                                                            {detalle.cve_producto}
                                                        </td>
                                                        <td style={{ 
                                                            verticalAlign: 'middle',
                                                            paddingLeft: '8px',
                                                            wordWrap: 'break-word'
                                                        }}>
                                                            {getDescripcionProducto(detalle.cve_producto)}
                                                        </td>
                                                        <td style={{ 
                                                            textAlign: 'center', 
                                                            verticalAlign: 'middle',
                                                            fontWeight: 'bold',
                                                            color: '#007bff',
                                                            width: '60px'
                                                        }}>
                                                            {detalle.cantidad}
                                                        </td>
                                                        {movimientoType === 'mantenimiento' && (
                                                            <td style={{ 
                                                                verticalAlign: 'middle',
                                                                paddingLeft: '8px',
                                                                wordWrap: 'break-word',
                                                                fontStyle: detalle.observaciones ? 'normal' : 'italic',
                                                                color: detalle.observaciones ? 'inherit' : '#6c757d'
                                                            }}>
                                                                {detalle.observaciones || '-'}
                                                            </td>
                                                        )}
                                                        {movimientoType === 'salida' && (
                                                            <>
                                                                <td style={{ 
                                                                    textAlign: 'center', 
                                                                    verticalAlign: 'middle',
                                                                    wordWrap: 'break-word',
                                                                    width: '90px',
                                                                    fontSize: '0.8rem'
                                                                }}>
                                                                    {getNombreSucursal(detalle.id_sucursal_destino) || '-'}
                                                                </td>
                                                                <td style={{ 
                                                                    textAlign: 'center', 
                                                                    verticalAlign: 'middle',
                                                                    wordWrap: 'break-word',
                                                                    fontSize: '0.85rem'
                                                                }}>
                                                                    {getNombreDepartamento(detalle.cve_departamento_destino) || '-'}
                                                                </td>
                                                                <td style={{ 
                                                                    textAlign: 'center', 
                                                                    verticalAlign: 'middle',
                                                                    wordWrap: 'break-word',
                                                                    fontWeight: '500',
                                                                    color: '#28a745'
                                                                }}>
                                                                    {detalle.nombre_usuario || '-'}
                                                                </td>
                                                            </>
                                                        )}
                                                    </tr>
                                                ));
                                            })()}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}

                        {movimientoType === 'mantenimiento' && (
                            <div style={{ marginTop: '3rem', textAlign: 'center' }}>
                                <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>
                                    _____________________________
                                </div>
                                <div style={{ fontWeight: 'bold', letterSpacing: '2px' }}>
                                    Firma de conformidad
                                </div>
                            </div>
                        )}
                    </>
                )}
            </Dialog>

            {/* Estilos CSS adicionales */}
            <style>
                {`
                    .p-datatable .p-datatable-tbody > tr {
                        cursor: pointer;
                        transition: background-color 0.2s ease;
                    }
                    
                    .p-datatable .p-datatable-tbody > tr:hover {
                        background-color: #f8f9fa !important;
                    }
                    
                    .border-right {
                        border-right: 1px solid #dee2e6;
                    }
                    
                    @media (max-width: 768px) {
                        .border-right {
                            border-right: none;
                            border-bottom: 1px solid #dee2e6;
                            margin-bottom: 1rem;
                            padding-bottom: 1rem;
                        }
                        
                        .content-wrapper {
                            background-attachment: scroll !important;
                        }
                    }
                `}
            </style>
        </>
    );
};

export default Inicio;